import { historicalProductName, historicalSku, historicalVariantOptions, hasLiveCatalogueIdentity, type CatalogueHistoryFields } from "@/lib/catalogue-history";
import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/base/PageHeader";
import { StatusBadge } from "@/components/base/StatusBadge";
import { LoadingState } from "@/components/base/LoadingState";
import { EmptyState } from "@/components/base/EmptyState";
import { Modal } from "@/components/base/Modal";
import { ConfirmDialog } from "@/components/base/ConfirmDialog";
import { fmtDateTime, variantLabel } from "@/lib/format";
import { friendlyError } from "@/lib/errors";
import { BarcodeScanInput } from "@/components/feature/BarcodeScanInput";
import { resolveBarcode } from "@/lib/barcode";
import { canOperateInventory } from "@/lib/permissions";
import { firstRelation } from "@/lib/relations";

interface SessionRow {
  id: string;
  session_number: string;
  location_id: string | null;
  status: string;
  count_type: string;
  notes: string | null;
  started_at: string | null;
  finalized_at: string | null;
  created_at: string;
  locations?: { name: string } | null;
}

interface CountItem extends CatalogueHistoryFields {
  id: string;
  product_id: string | null;
  variant_id: string | null;
  location_id: string;
  expected_qty: number;
  counted_qty: number | null;
  variance: number;
  is_recounted: boolean;
  recount_qty: number | null;
  counted_at: string | null;
  notes: string | null;
  products?: { name: string; sku: string | null; barcode: string | null } | null;
  variants?: { sku: string | null; barcode: string | null; option_values: Record<string, string> | null } | null;
}

interface StaleItem {
  item_id: string;
  product_id: string;
  product_name: string | null;
  variant_id: string | null;
  location_id: string;
  expected_qty: number;
  counted_qty: number;
  current_on_hand: number;
  movement_count: number | null;
  latest_movement_at: string | null;
  reason: string;
}

const balanceKey = (productId: string | null, variantId: string | null, locationId: string) =>
  `${productId}:${variantId ?? ""}:${locationId}`;

const inputCls = "w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500";
const labelCls = "block text-xs font-medium text-slate-500 mb-1";

export default function Stocktake() {
  const { org, role } = useAuth();
  const canMutate = canOperateInventory(role);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [activeSession, setActiveSession] = useState<SessionRow | null>(null);
  const [sessionItems, setSessionItems] = useState<CountItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [finalizeConfirm, setFinalizeConfirm] = useState(false);
  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const [scanFeedback, setScanFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [searchItem, setSearchItem] = useState("");
  const [form, setForm] = useState({ location_id: "", count_type: "full", notes: "" });
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [createIdemKey, setCreateIdemKey] = useState("");
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [staleItems, setStaleItems] = useState<StaleItem[]>([]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["stocktake-sessions", org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      const [sessions, locations] = await Promise.all([
        supabase
          .from("stock_count_sessions")
          .select("id, session_number, location_id, status, count_type, notes, started_at, finalized_at, created_at, locations(name)")
          .eq("org_id", org!.id)
          .order("created_at", { ascending: false }),
        supabase.from("locations").select("id, name").eq("org_id", org!.id).order("name"),
      ]);
      return {
        sessions: (sessions.data ?? []).map((row) => ({
          ...row,
          locations: firstRelation(row.locations),
        })) as SessionRow[],
        locations: (locations.data ?? []) as { id: string; name: string }[],
      };
    },
  });

  const loadSessionItems = async (session: SessionRow) => {
    setLoadingItems(true);
    setActiveSession(session);
    setStaleItems([]);
    const [itemsRes, balanceRes] = await Promise.all([
      supabase
        .from("stock_count_items")
        .select("catalogue_product_identity_id, catalogue_variant_identity_id, product_name_snapshot, sku_snapshot, variant_attributes_snapshot, catalogue_snapshot_origin, id, product_id, variant_id, location_id, expected_qty, counted_qty, variance, is_recounted, recount_qty, counted_at, notes, products(name, sku, barcode), variants(sku, barcode, option_values)")
        .eq("org_id", org!.id)
        .eq("session_id", session.id)
        .order("created_at"),
      supabase
        .from("inventory_balances")
        .select("product_id, variant_id, location_id, on_hand")
        .eq("org_id", org!.id),
    ]);
    const items = itemsRes.data;
    const balanceMap: Record<string, number> = {};
    for (const b of balanceRes.data ?? []) {
      balanceMap[balanceKey(b.product_id, b.variant_id ?? null, b.location_id)] = b.on_hand;
    }
    setBalances(balanceMap);
    setSessionItems((items ?? []).map((row) => ({
      ...row,
      products: firstRelation(row.products),
      variants: firstRelation(row.variants),
    })) as CountItem[]);
    setLoadingItems(false);
  };

  const handleCreateSession = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    const idemKey = createIdemKey || crypto.randomUUID();
    setCreateIdemKey(idemKey);

    // Trusted RPC: server-side session number, transactional header+items,
    // actor attribution, and idempotency — all in one operation.
    const { data: result, error: rpcErr } = await supabase.rpc("create_stock_count_session", {
      p_location_id: form.location_id || null,
      p_count_type: form.count_type,
      p_notes: form.notes || null,
      p_idempotency_key: idemKey,
    });

    setSaving(false);
    if (rpcErr) {
      setError(friendlyError(rpcErr.message));
      return;
    }

    const sessionId = result?.session_id as string | undefined;
    setCreateIdemKey("");
    setCreateOpen(false);
    setForm({ location_id: "", count_type: "full", notes: "" });
    queryClient.invalidateQueries({ queryKey: ["stocktake-sessions", org?.id] });

    if (!sessionId) return;
    const freshSession = await supabase
      .from("stock_count_sessions")
      .select("id, session_number, location_id, status, count_type, notes, started_at, finalized_at, created_at, locations(name)")
      .eq("id", sessionId)
      .maybeSingle();
    if (freshSession.data) {
      loadSessionItems({
        ...freshSession.data,
        locations: firstRelation(freshSession.data.locations),
      } as SessionRow);
    }
  };

  const updateCount = async (itemId: string, qty: number | null) => {
    const { error: rpcErr } = await supabase.rpc("set_stock_count_quantity", {
      p_item_id: itemId,
      p_counted_qty: qty,
    });
    if (rpcErr) {
      setError(friendlyError(rpcErr.message));
      return;
    }
    setSessionItems((prev) => prev.map((it) => it.id === itemId ? {
      ...it,
      counted_qty: qty,
      variance: (qty ?? 0) - it.expected_qty,
    } : it));
    setStaleItems([]);
  };

  const handleScan = async (value: string) => {
    setScanFeedback(null);
    try {
      const res = await resolveBarcode(org!.id, value);
      if (!res.found) {
        setScanFeedback({ ok: false, text: `No item found for "${value}"` });
        return;
      }
      const match = res.matches[0];
      const item = sessionItems.find((it) =>
        it.product_id === match.product_id &&
        (it.variant_id === match.variant_id || (!it.variant_id && !match.variant_id))
      );
      if (!item) {
        setScanFeedback({ ok: false, text: `"${match.name}" is not in this count session.` });
        return;
      }
      const newCount = (item.counted_qty ?? 0) + 1;
      await updateCount(item.id, newCount);
      setScanFeedback({ ok: true, text: `${match.name} — counted: ${newCount}` });
    } catch (err) {
      setScanFeedback({ ok: false, text: (err as Error).message });
    }
  };

  const handleFinalize = async () => {
    if (!activeSession) return;
    setFinalizeLoading(true);
    const { data: result, error: rpcErr } = await supabase.rpc("finalize_stock_count", { p_session_id: activeSession.id });
    setFinalizeLoading(false);
    setFinalizeConfirm(false);
    if (rpcErr) {
      setError(friendlyError(rpcErr.message));
      return;
    }
    if (result?.stale) {
      setStaleItems((result.stale_items ?? []) as StaleItem[]);
      return;
    }
    setStaleItems([]);
    queryClient.invalidateQueries({ queryKey: ["stocktake-sessions", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["inventory", org?.id] });
    queryClient.invalidateQueries({ queryKey: ["dashboard", org?.id] });
    setActiveSession(null);
    setSessionItems([]);
  };

  const handleCancelSession = async () => {
    if (!activeSession) return;
    setSaving(true);
    const { error: rpcErr } = await supabase.rpc("cancel_stock_count_session", { p_session_id: activeSession.id });
    setSaving(false);
    if (rpcErr) {
      setError(friendlyError(rpcErr.message));
      return;
    }
    setCancelConfirm(false);
    setActiveSession(null);
    setSessionItems([]);
    queryClient.invalidateQueries({ queryKey: ["stocktake-sessions", org?.id] });
  };

  const handleRecount = async (itemId: string) => {
    const { error: rpcErr } = await supabase.rpc("recount_stock_count_item", { p_item_id: itemId });
    if (rpcErr) {
      setError(friendlyError(rpcErr.message));
      return;
    }
    setSessionItems((prev) => prev.map((it) => it.id === itemId ? {
      ...it,
      is_recounted: true,
      recount_qty: null,
      counted_qty: null,
      variance: 0 - it.expected_qty,
    } : it));
    setStaleItems([]);
  };

  const handleZeroCount = async (itemId: string) => {
    await updateCount(itemId, 0);
  };

  const moveToReview = async () => {
    if (!activeSession) return;
    const { error: rpcErr } = await supabase.rpc("move_stock_count_to_review", { p_session_id: activeSession.id });
    if (rpcErr) {
      setError(friendlyError(rpcErr.message));
      return;
    }
    setActiveSession((prev) => prev ? { ...prev, status: "review" } : prev);
    queryClient.invalidateQueries({ queryKey: ["stocktake-sessions", org?.id] });
  };

  const handleReviewApprove = async () => {
    if (!activeSession) return;
    setFinalizeConfirm(true);
  };

  const filteredItems = useMemo(() => {
    if (!searchItem.trim()) return sessionItems;
    const q = searchItem.toLowerCase();
    return sessionItems.filter((it) =>
      historicalProductName(it).toLowerCase().includes(q) ||
      (historicalSku(it) ?? "").toLowerCase().includes(q) ||
      Object.values(historicalVariantOptions(it) ?? {}).some((v) => v.toLowerCase().includes(q))
    );
  }, [sessionItems, searchItem]);

  const variances = sessionItems.filter((it) => it.counted_qty !== null && it.variance !== 0);
  const uncounted = sessionItems.filter((it) => it.counted_qty === null);
  const counted = sessionItems.filter((it) => it.counted_qty !== null);

  return (
    <div>
      <PageHeader title="Stocktake" subtitle="Physical inventory counts with automatic variance and ledger adjustment">
        {!activeSession && canMutate && (
          <button onClick={() => setCreateOpen(true)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line" /> New Count Session
          </button>
        )}
        {activeSession && (
          <div className="flex items-center gap-2">
            <button onClick={() => { setActiveSession(null); setSessionItems([]); }} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">
              Back to sessions
            </button>
            {canMutate && activeSession.status === "in_progress" && (
              <>
                <button
                  onClick={moveToReview}
                  disabled={counted.length === 0}
                  className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
                >
                  <i className="ri-eye-line" /> Move to Review
                </button>
                <button
                  onClick={() => setCancelConfirm(true)}
                  className="px-4 py-2 bg-white border border-slate-200 hover:bg-red-50 hover:text-red-700 hover:border-red-200 text-slate-600 text-sm font-medium rounded-lg whitespace-nowrap"
                >
                  Cancel
                </button>
              </>
            )}
            {canMutate && ["in_progress", "review"].includes(activeSession.status) && (
              <button
                onClick={() => setFinalizeConfirm(true)}
                disabled={counted.length === 0}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap flex items-center gap-2"
              >
                <i className="ri-check-double-line" /> Finalize & Apply
              </button>
            )}
          </div>
        )}
      </PageHeader>

      {error && <div className="mb-4 px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

      {!activeSession ? (
        <>
          {isLoading ? (
            <LoadingState />
          ) : isError ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <p className="text-sm text-slate-500 mb-4">Couldn't load sessions.</p>
              <button onClick={() => refetch()} className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg whitespace-nowrap">Retry</button>
            </div>
          ) : (data?.sessions ?? []).length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200">
              <EmptyState icon="ri-list-check" title="No count sessions" description="Create a stock count session to audit physical inventory and reconcile variances." />
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Session</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Location</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Started</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Finalized</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data?.sessions ?? []).map((s) => (
                      <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="px-4 py-3 text-sm font-semibold text-slate-800 font-mono">{s.session_number}</td>
                        <td className="px-4 py-3 text-sm text-slate-600 capitalize">{s.count_type}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{s.locations?.name ?? "All locations"}</td>
                        <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                        <td className="px-4 py-3 text-sm text-slate-500">{s.started_at ? fmtDateTime(s.started_at) : "—"}</td>
                        <td className="px-4 py-3 text-sm text-slate-500">{s.finalized_at ? fmtDateTime(s.finalized_at) : "—"}</td>
                        <td className="px-4 py-3 text-right">
                          {["in_progress", "review", "finalized"].includes(s.status) && (
                            <button
                              onClick={() => loadSessionItems(s)}
                              className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium rounded-lg whitespace-nowrap"
                            >
                              {s.status === "finalized" ? "View" : "Continue"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-5">
          {/* Session header */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-lg font-bold text-slate-900 font-mono">{activeSession.session_number}</span>
                  <StatusBadge status={activeSession.status} />
                  <span className="text-sm text-slate-500 capitalize">{activeSession.count_type} count</span>
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  Location: <span className="font-medium text-slate-700">{activeSession.locations?.name ?? "All locations"}</span>
                  {activeSession.started_at && <> · Started {fmtDateTime(activeSession.started_at)}</>}
                </p>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="text-center">
                  <p className="text-2xl font-bold text-slate-900">{counted.length}</p>
                  <p className="text-xs text-slate-400">Counted</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{uncounted.length}</p>
                  <p className="text-xs text-slate-400">Remaining</p>
                </div>
                <div className="text-center">
                  <p className={`text-2xl font-bold ${variances.length > 0 ? "text-red-600" : "text-emerald-700"}`}>{variances.length}</p>
                  <p className="text-xs text-slate-400">Variances</p>
                </div>
              </div>
            </div>
          </div>

          {/* Stale count callout */}
          {staleItems.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 text-red-600 flex-shrink-0">
                  <i className="ri-restart-line" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-800">Stock changed after count — recount required</h3>
                  <p className="text-xs text-red-600 mt-1">
                    {staleItems.length} item{staleItems.length !== 1 ? "s" : ""} can't be finalized because inventory moved after they were counted. Recount them, then finalize again.
                  </p>
                  <div className="mt-3 space-y-2">
                    {staleItems.map((s) => (
                      <div key={s.item_id} className="flex items-center justify-between gap-3 text-sm bg-white border border-red-100 rounded-lg px-3 py-2">
                        <span className="text-slate-700">
                          {s.product_name ?? "Product"}
                          <span className="text-slate-500 ml-1">· counted {s.counted_qty}</span>
                          {s.reason === "not_observed" && <span className="text-slate-400 ml-1">· no count time recorded</span>}
                        </span>
                        <span className="text-red-600 font-medium whitespace-nowrap">current stock {s.current_on_hand}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Barcode scan area */}
          {canMutate && ["in_progress", "review"].includes(activeSession.status) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Scan items to increment count</h3>
              <BarcodeScanInput onScan={handleScan} feedback={scanFeedback} placeholder="Scan barcode or SKU — increments count by 1" />
            </div>
          )}

          {/* Items table */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
              <h3 className="text-sm font-semibold text-slate-800">Count items</h3>
              <div className="relative flex-1 max-w-xs">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                <input
                  value={searchItem}
                  onChange={(e) => setSearchItem(e.target.value)}
                  placeholder="Search product or SKU..."
                  className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                />
              </div>
            </div>
            {loadingItems ? (
              <div className="p-8"><LoadingState /></div>
            ) : filteredItems.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No items in this session.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Product</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Expected</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Counted</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Current</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Variance</th>
                      <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-32">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((it) => {
                      const varColor = it.counted_qty === null ? "" :
                        it.variance > 0 ? "text-emerald-600" :
                        it.variance < 0 ? "text-red-600" : "text-slate-500";
                      const canEdit = canMutate && ["in_progress", "review"].includes(activeSession.status);
                      return (
                        <tr key={it.id} className={`border-b border-slate-50 ${it.counted_qty === null ? "bg-amber-50/20" : it.variance !== 0 ? "bg-red-50/10" : ""}`}>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-slate-800">
                              {historicalProductName(it)}
                              {(variantLabel(historicalVariantOptions(it)) || historicalSku(it)) && (
                                <span className="text-slate-500 font-normal ml-1">· {variantLabel(historicalVariantOptions(it)) || historicalSku(it)}</span>
                              )}
                            </p>
                            <p className="text-xs text-slate-400 font-mono">{historicalSku(it) ?? "—"}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600 text-right">{it.expected_qty}</td>
                          <td className="px-4 py-3 text-sm text-slate-800 text-right font-medium">
                            {it.counted_qty ?? <span className="text-slate-400">Not counted</span>}
                          </td>
                          <td className={`px-4 py-3 text-sm text-right font-medium ${balances[balanceKey(it.product_id, it.variant_id, it.location_id)] !== it.expected_qty ? "text-amber-600" : "text-slate-600"}`}>
                            {hasLiveCatalogueIdentity(it) ? (balances[balanceKey(it.product_id, it.variant_id, it.location_id)] ?? 0) : "Retired"}
                          </td>
                          <td className={`px-4 py-3 text-sm text-right font-semibold ${varColor}`}>
                            {it.counted_qty !== null ? (it.variance >= 0 ? "+" : "") + it.variance : "—"}
                          </td>
                          <td className="px-4 py-3">
                            {canEdit && (
                              <div className="flex items-center gap-1">
                                <input
                                  type="number"
                                  min="0"
                                  value={it.counted_qty ?? ""}
                                  onChange={(e) => {
                                    const v = e.target.value === "" ? null : parseInt(e.target.value, 10);
                                    if (v === null || !Number.isNaN(v)) updateCount(it.id, v);
                                  }}
                                  placeholder="Enter count"
                                  className="w-24 px-2 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-right"
                                />
                                <button
                                  onClick={() => handleZeroCount(it.id)}
                                  className="px-2 py-1.5 text-xs bg-white border border-slate-200 hover:bg-red-50 text-red-600 rounded-lg whitespace-nowrap"
                                  title="Mark as zero count"
                                >
                                  Zero
                                </button>
                                {it.counted_qty !== null && (
                                  <button
                                    onClick={() => handleRecount(it.id)}
                                    className="px-2 py-1.5 text-xs bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg whitespace-nowrap"
                                    title="Clear and recount"
                                  >
                                    Recount
                                  </button>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Variance summary */}
          {variances.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="text-sm font-semibold text-slate-800 mb-3">Variance vs snapshot</h3>
              <p className="text-xs text-slate-500 mb-3">
                {variances.length} item{variances.length !== 1 ? "s" : ""} differ from their snapshot quantity. Final adjustments are computed against live stock when you finalize — if stock moved since a count, finalization will ask you to recount instead of silently applying the old count.
              </p>
              <div className="space-y-2">
                {variances.map((it) => (
                  <div key={it.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-lg px-3 py-2">
                    <span className="text-slate-700">
                      {historicalProductName(it)}
                      {(variantLabel(historicalVariantOptions(it)) || historicalSku(it)) ? <span className="text-slate-500 ml-1">· {variantLabel(historicalVariantOptions(it)) || historicalSku(it)}</span> : null}
                    </span>
                    <span className={`font-semibold ${it.variance > 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {it.expected_qty} → {it.counted_qty} ({it.variance >= 0 ? "+" : ""}{it.variance})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New Count Session"
        footer={
          <>
            <button onClick={() => setCreateOpen(false)} className="px-4 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg whitespace-nowrap">Cancel</button>
            <button onClick={handleCreateSession} disabled={saving} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg whitespace-nowrap">
              {saving ? "Creating..." : "Start Count Session"}
            </button>
          </>
        }
      >
        <form onSubmit={handleCreateSession} className="space-y-4">
          {error && <div className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}
          <div>
            <label className={labelCls}>Count type</label>
            <select value={form.count_type} onChange={(e) => setForm((f) => ({ ...f, count_type: e.target.value }))} className={inputCls}>
              <option value="full">Full count — all products at all locations</option>
              <option value="cycle">Cycle count — specific location</option>
              <option value="spot">Spot check — manual items</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Location (leave blank for all locations)</label>
            <select value={form.location_id} onChange={(e) => setForm((f) => ({ ...f, location_id: e.target.value }))} className={inputCls}>
              <option value="">All locations</option>
              {(data?.locations ?? []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} className={inputCls} rows={2} maxLength={500} placeholder="Reason for count, area being counted, etc." />
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
            <i className="ri-information-line mr-1" />
            Creating a session captures a snapshot of current on-hand quantities. Inventory is NOT modified until you finalize the count and confirm the variances.
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={cancelConfirm}
        title="Cancel stock count session"
        message={`Cancel ${activeSession?.session_number}? This will discard all counted quantities. No inventory adjustments will be made.`}
        confirmLabel="Cancel Session"
        loading={saving}
        onCancel={() => setCancelConfirm(false)}
        onConfirm={handleCancelSession}
      />

      <ConfirmDialog
        open={finalizeConfirm}
        title="Finalize stock count"
        message={`Apply all ${variances.length} variance${variances.length !== 1 ? "s" : ""} as inventory adjustments? This will post ledger entries and update on-hand quantities. This action cannot be undone.`}
        confirmLabel="Finalize & Apply Adjustments"
        loading={finalizeLoading}
        onCancel={() => setFinalizeConfirm(false)}
        onConfirm={handleFinalize}
      />
    </div>
  );
}